import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject, take } from 'rxjs';

import { PrivaButtonComponent } from '@priva/components/button';
import {
    PrivaFilterSearchConfig,
    PrivaQueryableDataSource,
    PrivaQueryableDataSourceFactory,
    PrivaQueryUtils,
} from '@priva/components/filter';
import { PrivaInputModule } from '@priva/components/input';
import { PrivaNotificationModule } from '@priva/components/notification';
import { PrivaTitleComponent } from '@priva/components/title';
import { PrivaLocalizationModule, PrivaLocalizationService } from '@priva/localization';

import { ManualFlowFooterComponent } from 'app/features/manual-input-registration/components';
import { TerminalOverlayComponent } from 'app/shared/components';
import { selectContainerList } from 'app/state/containers/container.selectors';

import { Activity, defaultActivity } from '../activity-selector/models';
import { Container } from '../container-selector/models';
import { ContainerListComponent } from './components/container-list/container-list.component';
import { ContainerSelectorStore } from './container-selector.store';

@Component({
    selector: 'app-container-selector',
    imports: [
        FormsModule,
        PrivaButtonComponent,
        PrivaInputModule,
        PrivaLocalizationModule,
        PrivaNotificationModule,
        PrivaTitleComponent,
        ContainerListComponent,
        ManualFlowFooterComponent,
        TerminalOverlayComponent,
    ],
    templateUrl: './container-selector.component.html',
    styleUrl: './container-selector.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ContainerSelectorStore],
})
export class ContainerSelectorComponent {
    private store = inject(Store);
    protected containerSelectorStore = inject(ContainerSelectorStore);
    private destroyRef = inject(DestroyRef);
    private dataSourceFactory = inject(PrivaQueryableDataSourceFactory);
    private queryUtils = inject(PrivaQueryUtils);
    private privaLocalizationService = inject(PrivaLocalizationService);

    title = input('');
    multiple = input(false);
    activity = input<Activity | undefined>(defaultActivity());

    containersSelected = output<Container[]>();
    closeRequested = output();
    finishRequested = output();

    // Priva search input
    readonly containerDataSource: PrivaQueryableDataSource<Container>;
    filterSearchConfig: PrivaFilterSearchConfig = {
        type: 'search',
        id: 'searchContainer',
        title: 'GENERAL.TITLE.SEARCH_CONTAINER',
        placeholder: 'GENERAL.TITLE.SEARCH_CONTAINER',
        options: ['number'],
        searchOptions: {
            matchType: 'single',
        },
    };

    private continueRequested$ = new Subject<void>();
    errorMessage = '';
    containerNumberSearch: number | undefined;

    constructor() {
        this.containerDataSource = this.dataSourceFactory.create();
        this.containerDataSource.setData(this.store.select(selectContainerList));
        this.continueRequested$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.containersSelected.emit(this.containerSelectorStore.selectedContainers());
        });
        this.containerSelectorStore.setAvailableContainers(this.store.select(selectContainerList));
    }

    onSearch(search = NaN) {
        return this.containerDataSource.addSearch(
            this.queryUtils.mapSearchInputForQuery(this.filterSearchConfig, search.toString()),
        );
    }

    onReset(): void {
        this.containerNumberSearch = undefined;
        this.errorMessage = '';
        this.onSearch();
    }

    onContainerSelected(): void {
        if (this.containerNumberSearch === undefined) return;
        this.onSearch(this.containerNumberSearch);

        this.containerDataSource.results$
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((containers) => {
                const container = containers.find(({ number }) => {
                    return number === this.containerNumberSearch;
                });

                if (!container) {
                    this.errorMessage = this.privaLocalizationService.instant(
                        'ERROR.TITLE.CONTAINER_NUMBER_NOT_FOUND',
                        {
                            number: this.containerNumberSearch,
                        },
                    );
                    return;
                }

                if (this.containerSelectorStore.hasContainerBeenSelected(container)) {
                    this.errorMessage = this.privaLocalizationService.instant(
                        'ERROR.TITLE.CONTAINER_NUMBER_HAS_BEEN_ADDED',
                        {
                            number: this.containerNumberSearch,
                        },
                    );
                    return;
                }

                this.containerSelectorStore.addSelectedContainers(container);
                this.onReset();
            });
    }

    onContainerRemoved(container: Container): void {
        this.containerSelectorStore.removeSelectedContainer(container);
        if (!this.containerSelectorStore.selectedContainers().length) this.onReset();
    }

    onContinueRequested(): void {
        this.continueRequested$.next();
    }

    onCloseRequested(): void {
        this.closeRequested.emit();
    }

    onFinishRequested(): void {
        this.finishRequested.emit();
    }

    addContainer(container: Container): void {
        // Disallow adding containers if the activity only requires one container
        if (!this.multiple() && this.containerSelectorStore.selectedContainers().length > 0) return;
        this.containerSelectorStore.addSelectedContainers(container);
    }
}
